import {
  TIPOS_AUSENCIA,
  ausenciaSchema,
  feriadosFixosDoAno,
  importarFeriadosSchema,
} from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CalendarPlus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import {
  BarraTitulo,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Button } from '@/components/ui/button'
import { Campo, Input, MensagemFormulario, Select } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { toast } from '@/components/ui/toast'
import { formatarData, paraInputData } from '@/lib/datas'
import { mensagemDeErro } from '@/lib/erros'
import { ROTULO_AUSENCIA, plural } from '@/lib/formato'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/ausencias')({ component: Ausencias })

type Item = NonNullable<ReturnType<typeof useAusencias>['data']>[number]
function useAusencias() {
  const trpc = useTRPC()
  return useQuery(trpc.cadastros.ausencias.listar.queryOptions({}))
}

/**
 * Ausências (RF-C08): feriados da fábrica (valem para todos) e atestado,
 * férias ou outra ausência de um operador. Dia com ausência não é falta nem
 * conta tempo disponível na avaliação; quem trabalhou mesmo assim é avaliado.
 */
function Ausencias() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useAusencias()
  const operadores = useQuery(trpc.cadastros.usuarios.listar.queryOptions())
  const g = useGavetaCadastro<Item>()
  const [tipo, setTipo] = useState<(typeof TIPOS_AUSENCIA)[number]>('FERIADO')
  const [importando, setImportando] = useState(false)
  // O ano vive como texto enquanto a pessoa digita ("2", "20", "202"…);
  // só vira número válido na hora de listar e importar.
  const anoCorrente = new Date().getFullYear()
  const [anoTexto, setAnoTexto] = useState(String(anoCorrente))
  const [mensagem, setMensagem] = useState<{ erro?: string; sucesso?: string }>({})
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.ausencias.listar.queryKey() })
  const importar = useMutation(
    trpc.cadastros.ausencias.importarFeriadosNacionais.mutationOptions({
      onSuccess: (r) => {
        toast.sucesso(
          r.criados === 0
            ? `Os ${r.existentes} feriados nacionais de ${r.ano} já estavam cadastrados.`
            : `${plural(r.criados, 'feriado criado', 'feriados criados')} em ${r.ano}${
                r.existentes > 0 ? `; ${plural(r.existentes, 'já existia', 'já existiam')}` : ''
              }.`,
        )
        setImportando(false)
        void invalidar()
      },
      onError: (e) => setMensagem({ erro: mensagemDeErro(e) }),
    }),
  )
  const anoLido = importarFeriadosSchema.safeParse({ ano: Number(anoTexto) })
  const ano = anoLido.success ? anoLido.data.ano : null
  const feriadosDoAno = ano === null ? [] : feriadosFixosDoAno(ano)
  const abrirImportacao = () => {
    setMensagem({})
    setAnoTexto(String(anoCorrente))
    setImportando(true)
  }
  const criar = useMutation(
    trpc.cadastros.ausencias.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const remover = useMutation(
    trpc.cadastros.ausencias.remover.mutationOptions({
      onSuccess: () => {
        toast.sucesso('Ausência removida.')
        void invalidar()
      },
      onError: (e) => toast.erro(mensagemDeErro(e)),
    }),
  )
  const submit = aoSubmeter({
    schema: ausenciaSchema,
    opcionais: ['operadorId', 'descricao'],
    setErros: g.setErros,
    sucesso: 'Ausência cadastrada.',
    onValido: (d) => criar.mutateAsync(d),
  })
  const feriado = tipo === 'FERIADO'

  return (
    <>
      <BarraTitulo
        titulo="Ausências e feriados"
        descricao="Feriado vale para toda a fábrica; atestado, férias e outras ausências são por operador. Nesses dias não há falta nem jornada na avaliação."
        onNovo={() => {
          setTipo('FERIADO')
          g.abrirNovo()
        }}
        rotuloNovo="Nova ausência"
        acoes={
          <Button variant="secondary" onClick={abrirImportacao}>
            <CalendarPlus className="size-4" aria-hidden /> Importar feriados nacionais de{' '}
            {anoCorrente}
          </Button>
        }
      />
      {mensagem.erro && (
        <div className="mb-4">
          <MensagemFormulario erro={mensagem.erro} />
        </div>
      )}
      <Tabela>
        <Cabecalho>
          <Th>Período</Th>
          <Th>Tipo</Th>
          <Th>Operador</Th>
          <Th>Descrição</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((a) => (
            <Linha key={a.id}>
              <Td numerico>
                {formatarData(a.inicio)}
                {paraInputData(a.fim) !== paraInputData(a.inicio) && ` a ${formatarData(a.fim)}`}
              </Td>
              <Td className="font-medium">{ROTULO_AUSENCIA[a.tipo]}</Td>
              <Td className="text-fg-muted">{a.operador?.nome ?? 'Toda a fábrica'}</Td>
              <Td className="text-fg-muted">{a.descricao ?? '—'}</Td>
              <Td className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={`Remover ${ROTULO_AUSENCIA[a.tipo].toLowerCase()} de ${formatarData(a.inicio)}`}
                  onClick={() => remover.mutate({ id: a.id })}
                >
                  <Trash2 className="size-4" aria-hidden />
                </Button>
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={5} />}
          {lista.data?.length === 0 && <Vazio colunas={5}>Nenhuma ausência cadastrada.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo="Nova ausência"
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta form="form-ausencia" salvando={criar.isPending} onCancelar={g.fechar} />
        }
      >
        <Formulario
          id="form-ausencia"
          onSubmit={submit}
          erros={g.erros}
          onLimparErro={g.limparErro}
        >
          <Campo label="Tipo" erro={g.erros.tipo}>
            {(p) => (
              <Select
                {...p}
                name="tipo"
                value={tipo}
                onChange={(ev) => setTipo(ev.target.value as (typeof TIPOS_AUSENCIA)[number])}
                autoFocus
              >
                {TIPOS_AUSENCIA.map((t) => (
                  <option key={t} value={t}>
                    {ROTULO_AUSENCIA[t]}
                  </option>
                ))}
              </Select>
            )}
          </Campo>
          <Campo
            label="Operador"
            erro={g.erros.operadorId}
            {...(feriado ? { dica: 'Feriado vale para toda a fábrica.' } : {})}
          >
            {(p) => (
              <Select {...p} name="operadorId" defaultValue="" disabled={feriado}>
                <option value="">{feriado ? 'Toda a fábrica' : 'Escolha…'}</option>
                {operadores.data
                  ?.filter((u) => u.perfil === 'OPERADOR' && u.ativo)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.nome}
                    </option>
                  ))}
              </Select>
            )}
          </Campo>
          <div className="grid grid-cols-2 gap-3">
            <Campo label="Início" erro={g.erros.inicio}>
              {(p) => <Input {...p} name="inicio" type="date" />}
            </Campo>
            <Campo label="Fim" erro={g.erros.fim} dica="Mesmo dia para um só dia.">
              {(p) => <Input {...p} name="fim" type="date" />}
            </Campo>
          </div>
          <Campo
            label="Descrição"
            erro={g.erros.descricao}
            dica="Opcional (ex.: Consulta, Carnaval)."
          >
            {(p) => <Input {...p} name="descricao" maxLength={120} />}
          </Campo>
        </Formulario>
      </Gaveta>
      <Gaveta
        aberta={importando}
        titulo="Importar feriados nacionais"
        onFechar={() => setImportando(false)}
        rodape={
          <>
            <Button
              variant="secondary"
              onClick={() => setImportando(false)}
              disabled={importar.isPending}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => ano !== null && importar.mutate({ ano })}
              disabled={ano === null}
              carregando={importar.isPending}
            >
              {ano === null ? 'Importar' : `Importar ${feriadosDoAno.length} feriados`}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <Campo
            label="Ano"
            dica="Cria uma ausência de fábrica para cada feriado que ainda não estiver cadastrado; rodar de novo não duplica."
            {...(ano === null ? { erro: 'Informe um ano entre 2000 e 2100' } : {})}
          >
            {(p) => (
              <Input
                {...p}
                type="number"
                inputMode="numeric"
                min={2000}
                max={2100}
                value={anoTexto}
                onChange={(ev) => setAnoTexto(ev.target.value)}
                autoFocus
              />
            )}
          </Campo>
          {ano !== null && (
            <div>
              <p className="text-sm font-medium">Feriados de data fixa de {ano}</p>
              <ul className="mt-2 flex flex-col gap-1 text-sm text-fg-muted">
                {feriadosDoAno.map((f) => (
                  <li key={f.data} className="flex gap-3">
                    <span className="tabular">{formatarData(f.data)}</span>
                    <span>{f.nome}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="text-sm text-fg-muted">
            Carnaval, Sexta-feira Santa e Corpus Christi mudam de data a cada ano e dependem de a
            fábrica parar: cadastre-os em “Nova ausência”. Feriados municipais e estaduais também.
          </p>
        </div>
      </Gaveta>
    </>
  )
}
